import { useCallback, useMemo } from 'react';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import { formatDate, formatDateTimeFromDate, formatTime } from '@/lib/core/utils';
import { CheckoutContexts } from '@/services/platform/data/checkout';
import { getRide } from '@/services/platform/data/booking/rides';
import { CheckoutScreen } from '@/components/shared/checkout/CheckoutScreen';
import type { CheckoutPurchase } from '@/components/shared/checkout/types';
import type { RangeRoverPaymentProps } from './types';

// ── RangeRoverPayment ─────────────────────────────────────────────────────────

/**
 * The payment step for a Range Rover ride booked against a paid service category.
 *
 * A paid ride comes back from `createRide` with `status: 'AWAITING_PAYMENT'` and is deliberately
 * not sent to Onde yet (see `RidePaymentContextHandler`) — no car is asked for until the fare is
 * held or charged. This adapts the generic `CheckoutScreen` over the `'ride'` payment context, the
 * same way `EventCheckoutScreen`/`MewsCheckoutScreen` adapt it for their own contexts — but embedded
 * inline as a flow step rather than reached via the dedicated `/checkout` route: `onBack` steps back
 * within the flow instead of navigating away, and there is no CMS `checkout` page content in scope
 * here (`CheckoutScreen` already degrades gracefully without it).
 */
export function RangeRoverPayment({ booking, context, onBack, onSuccess }: RangeRoverPaymentProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const ride = booking.ride;

  const purchase: CheckoutPurchase | undefined = useMemo(() => {
    if (!ride) return undefined;

    const pickup = booking.pickupPoint?.name ?? ride.pickupName ?? ride.pickupAddress ?? undefined;
    const dropoff = booking.dropoffPoint?.name ?? ride.dropoffName ?? ride.dropoffAddress ?? undefined;
    const when =
      booking.date && booking.time
        ? `${formatDate(booking.date.toISOString())}, ${formatTime(booking.time)}`
        : formatDateTimeFromDate(ride.pickupTime, locale);

    return {
      context: CheckoutContexts.Ride,
      contextReference: ride.publicGuid,
      amountMajor: ride.estimatedCost ?? 0,
      currencyCode: ride.currency ?? undefined,
      // Unused: `onBack` below overrides every navigation-away path `CheckoutScreen` has.
      returnPath: '/',
      summaryRows: [
        ...(pickup ? [{ label: t('App.Labels.WhereFrom'), value: pickup }] : []),
        ...(dropoff ? [{ label: t('App.Labels.WhereTo'), value: dropoff }] : []),
        ...(when ? [{ label: t('App.Labels.When'), value: when }] : []),
      ],
    };
  }, [ride, booking.pickupPoint, booking.dropoffPoint, booking.date, booking.time, locale, t]);

  // Payment success carries no `RideDto` of its own — the backend dispatches the ride to Onde as
  // part of securing the fare (`RidePaymentContextHandler.OnPaymentSecuredAsync`), so the fresh fetch
  // is what carries the post-dispatch status forward to the next step.
  const handleSucceeded = useCallback(async () => {
    if (!ride) return;
    const fresh = await getRide(ride.publicGuid, locale);
    onSuccess(fresh ?? ride);
  }, [ride, locale, onSuccess]);

  return (
    <CheckoutScreen
      purchase={purchase}
      context={context}
      onSucceeded={handleSucceeded}
      onBack={onBack}
      embedded
    />
  );
}
